import React, { useContext, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthContext } from '@/contexts/auth-context'
import axiosInstance from '@/api/axiosInstance'
import {
  User,
  PieChart as PieIcon, // Aliased to avoid conflict with Recharts
  Map,
  BarChart as BarIcon, // Aliased to avoid conflict with Recharts
  TrendingUp,
  Eye,
  Grid3X3,
  ChevronRight,
  Users,
  MapPin,
  Calendar,
  ShoppingCart,
  Wallet,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  PieChart as RechartsPieChart, // Renamed to avoid conflict
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart as RechartsBarChart, // Renamed to avoid conflict
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import logger from '../../utils/logger'

// Define the new color palette
const THEME_COLORS = {
  royalBlue: '#305CDE',
  purple: '#7C007C',
  lilac: '#A47DAB',
  white: '#FFFFFF',
  purpleLight: '#911C9C',
}

function BusinessDashboard() {
  const { auth } = useContext(AuthContext)
  const businessId = auth.user._id // Dynamic businessId from AuthContext
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState('cards') // 'cards' or 'charts'
  const location = useLocation()
  const queryClient = useQueryClient()

  // 1. Get customer demographic data (Dynamic API Call)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customerDemographics', businessId],
    queryFn: async () => {
      const response = await axiosInstance.get('reports/my-customers/demographics')
      return response.data.data
    },
    // Only fetch if businessId is available
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  })

  // 2. Get most recent customers (Dynamic API Call)
  const {
    data: recentCustomersData,
    isLoading: loadingRecent,
    isError: errorRecent,
  } = useQuery({
    queryKey: ['recentCustomers', businessId],
    queryFn: async () => {
      const res = await axiosInstance.get('reports/my-customers/list', {
        params: {
          limit: 5,
          page: 1,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      return res.data?.data?.customers || []
    },
    // Only fetch if businessId is available
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  })

  const handleClick = () => {
    navigate('/business/my-customers')
  }

  const handleClickOnPurchase = () => {
    navigate('/business/reports/customer/purchase')
  }

  // Dynamic data values
  const totalCustomers = data?.totalCustomers || 0
  const ageDistribution = data?.ageDistribution || {}
  const genderDistribution = data?.genderDistribution || {}
  const locationDistribution = data?.locationDistribution || []
  const totalPurchased = data?.totalPurchased || 0
  const totalRedemptionPoints = data?.totalPoints || 0
  const genderData = data?.genderCounts

  // Transform data for charts
  const genderChartData = [
    { name: 'Male', value: genderDistribution?.male || 0, color: THEME_COLORS.royalBlue },
    { name: 'Female', value: genderDistribution?.female || 0, color: THEME_COLORS.purpleLight },
    { name: 'Other', value: genderDistribution?.other || 0, color: THEME_COLORS.lilac },
  ]

  const ageChartData = Object.entries(ageDistribution).map(([age, count]) => ({
    age,
    count,
  }))

  // Limit location data for chart readability and shorten long names
  const locationChartData = locationDistribution.slice(0, 10).map((item) => ({
    location: item.location.length > 10 ? item.location.substring(0, 10) + '...' : item.location,
    count: item.count,
  }))

  // Use the new colors for the COLORS array for consistent styling
  const CHART_COLORS = [
    THEME_COLORS.royalBlue,
    THEME_COLORS.purpleLight,
    THEME_COLORS.lilac,
    '#10B981', // Keeping a vibrant green for variety
    '#F59E0B',
    '#EF4444',
    '#06B6D4',
    '#84CC16',
  ]

  useEffect(() => {
    if (location.pathname === '/business/dashboard') {
      queryClient.invalidateQueries(['customerDemographics', businessId])
      queryClient.invalidateQueries(['recentCustomers', businessId])
    }
  }, [location.pathname, businessId, queryClient])

  // Render loading/error state for top-level data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-gray-700">Loading dashboard data...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-red-500 text-lg">
          Error loading dashboard data. Please try again later.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0F2F5' }}>
      {' '}
      {/* Light background for the whole page */}
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1
                className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right, ${THEME_COLORS.royalBlue}, ${THEME_COLORS.purple})`,
                }}
              >
                Dashboard Overview
              </h1>
              <p className="text-gray-600 mt-2 text-lg">
                Monitor your business performance at a glance
              </p>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-50 dark:bg-white rounded-xl shadow-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'cards'
                    ? 'bg-theme-color-800 text-white shadow-md'
                    : 'text-gray-600 hover:text-theme-color-800'
                }`}
              >
                <Grid3X3 size={16} />
                Cards
              </button>
              <button
                onClick={() => setViewMode('charts')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'charts'
                    ? 'bg-theme-color-800 text-white shadow-md'
                    : 'text-gray-600 hover:text-theme-color-800'
                }`}
              >
                <BarIcon size={16} />
                Charts
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Total Customers Card */}
          <div
            onClick={handleClick}
            role="button"
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-xl bg-theme-blue/10">
                    <Users size={20} style={{ color: THEME_COLORS.royalBlue }} />
                  </div>
                  <h3 className="text-gray-600 font-medium">Total Customers</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {totalCustomers.toLocaleString()}
                </p>
                {/* <p className="text-sm text-green-600 mt-1">+12% from last month</p> */}
              </div>
              <TrendingUp className="text-green-500" size={24} />
            </div>
          </div>

          {/* Total purchase history */}
          <div
            onClick={handleClickOnPurchase}
            role="button"
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: `${THEME_COLORS.royalBlue}1A` }}
                  >
                    {' '}
                    {/* 10% opacity */}
                    <ShoppingCart size={20} style={{ color: THEME_COLORS.royalBlue }} />
                  </div>
                  <h3 className="text-gray-600 font-medium">Total Purchase</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {totalPurchased.toLocaleString()}
                </p>
                {/* <p className="text-sm text-green-600 mt-1">+12% from last month</p> */}
              </div>
              <TrendingUp className="text-green-500" size={24} />
            </div>
          </div>

          {/* Redemption Points */}
          <div
            // onClick={handleClickOnPurchase}
            // role="button"
            className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: `${THEME_COLORS.royalBlue}1A` }}
                  >
                    {' '}
                    {/* 10% opacity */}
                    <Wallet size={20} style={{ color: THEME_COLORS.royalBlue }} />
                  </div>
                  <h3 className="text-gray-600 font-medium">Total Redemption</h3>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {totalRedemptionPoints.toLocaleString()}
                </p>
                {/* <p className="text-sm text-green-600 mt-1">+12% from last month</p> */}
              </div>
              <TrendingUp className="text-green-500" size={24} />
            </div>
          </div>

          {/* Gender Distribution Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-xl"
                  style={{ backgroundColor: `${THEME_COLORS.purpleLight}1A` }}
                >
                  <Users size={20} style={{ color: THEME_COLORS.purpleLight }} />
                </div>
                <h3 className="text-gray-600 font-medium">Gender Split</h3>
              </div>
            </div>

            <div className="flex justify-between items-center px-2">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Male</p>
                <p className="text-2xl font-bold text-gray-900">{genderData?.Male || 0}</p>
              </div>
              <div className="h-10 w-px bg-gray-100"></div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Female</p>
                <p className="text-2xl font-bold text-gray-900">
                  {genderData?.Female || 0}
                </p>
              </div>
              <div className="h-10 w-px bg-gray-100"></div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Other</p>
                <p className="text-2xl font-bold text-gray-900">{genderData?.Other || 0}</p>
              </div>
            </div>
          </div>

          {/* Top Location Card */}
          {/* <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-xl" style={{ backgroundColor: `#10B9811A` }}>
                    {' '}
                    <MapPin size={20} style={{ color: '#10B981' }} />
                  </div>
                  <h3 className="text-gray-600 font-medium">Top Location</h3>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {locationDistribution?.length ? locationDistribution[0].location : 'N/A'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {locationDistribution?.length
                    ? `${locationDistribution[0].count} customers`
                    : 'No data'}
                </p>
              </div>
            </div>
          </div> */}

          {/* Age Groups Card */}
          {/* <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: `${THEME_COLORS.lilac}1A` }}
                  >
                    <Calendar size={20} style={{ color: THEME_COLORS.lilac }} />
                  </div>
                  <h3 className="text-gray-600 font-medium">Age Groups</h3>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {Object.keys(ageDistribution).length} Groups
                </p>
                <p className="text-sm text-gray-500 mt-1">Age distribution categories</p>
              </div>
            </div>
          </div> */}
        </div>

        {/* Dynamic Content based on ViewMode */}
        {viewMode === 'cards' ? (
          <>
            {/* Age Distribution Section - Cards */}
            {/* <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Age Distribution</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Eye size={16} />
                  Card View
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {Object.entries(ageDistribution).map(([ageGroup, count]) => (
                  <div
                    key={ageGroup}
                    className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{ageGroup}</h3>
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${THEME_COLORS.lilac}1A` }}
                      >
                        <BarIcon size={16} style={{ color: THEME_COLORS.lilac }} />{' '}
                      </div>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: THEME_COLORS.lilac }}>
                      {count}
                    </p>
                    <p className="text-sm text-gray-500">customers</p>
                  </div>
                ))}
              </div>
            </div> */}
          </>
        ) : (
          <>
            {/* Age Distribution Section - Chart */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Age Distribution</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Eye size={16} />
                  Chart View
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={ageChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="age" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: THEME_COLORS.white,
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Bar dataKey="count" fill={THEME_COLORS.royalBlue} radius={[4, 4, 0, 0]} />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Gender and Location Distribution Charts (always shown in chart view, or can be toggled) */}
        {viewMode === 'charts' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Gender Distribution</h3>
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart>
                  <Pie
                    data={genderChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {genderChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top Locations</h3>
              <ResponsiveContainer width="100%" height={250}>
                <RechartsBarChart data={locationChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="location" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Location Distribution Table (Always visible, or you can toggle this too) */}
        {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-12">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Location Distribution (Table)</h2>
          </div>
          {isLoading ? ( 
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading location data...</p>
            </div>
          ) : isError || !locationDistribution.length ? (
            <div className="p-6 text-center text-gray-500">
              <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>No location data available.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customers
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {locationDistribution.map((entry, index) => (
                    <tr key={entry.location} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full mr-3`}
                            style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                          ></div>
                          <span className="text-sm font-medium text-gray-900">
                            {entry.location}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalCustomers > 0 ? ((entry.count / totalCustomers) * 100).toFixed(1) : 0}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div> */}

        {/* Recent Customers Table (Always visible) */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Recent Customers</h2>
              <button
                onClick={() => navigate('/business/my-customers')}
                className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg bg-theme-color-800 hover:opacity-90 transition-colors"
              >
                View All
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {loadingRecent ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading recent customers...</p>
            </div>
          ) : errorRecent ? (
            <div className="p-6 text-center text-red-500">
              <p>Failed to load recent customers.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentCustomersData.map((customer) => (
                    <tr key={customer._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                            style={{
                              backgroundImage: `linear-gradient(to right, ${THEME_COLORS.royalBlue}, ${THEME_COLORS.purpleLight})`,
                            }}
                          >
                            {customer.name ? customer.name.charAt(0).toUpperCase() : 'N'}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {customer.name || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customer.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customer.mobile || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            customer.status === 'active'
                              ? 'bg-green-100 text-green-800' // Keeping green for active status as it's a common convention
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {customer.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customer.createdAt
                          ? new Date(customer.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BusinessDashboard
